<template>
    <div
        class="grid gap-6 grid-cols-1 sm:grid-cols-2"
        :class="{
            'lg:grid-cols-4': readMode,
            'lg:grid-cols-5': !readMode,
        }"
    >
        <div class="col-span-2">
            <v-text-field
                v-model="offerBasis.clientName"
                :label="
                    $t('offerCommonOptions.Customers first and last name')
                "
                variant="outlined"
                density="compact"
                :disabled="readMode"
            />

            <template v-if="subsidyModel !== null">
                <p>{{ $t('heatPumpOffersCreate.Subsidies') }}</p>
                <ul class="list">
                    <li>
                        <input checked type="radio" id="subsidy_active" v-model="subsidyModel.selected" value="active" class="mr-1">
                        <label for="subsidy_active" class="ml-2">{{ subsidyModel.active.title }}: <template
                            v-if="!subsidyModel.active.is_automatic"
                        >{{ subsidyModel.active.amount }} {{ currencySymbol }}</template><template v-else><b>Calculated based on installation size</b></template></label>
                    </li>
                    <li>
                        <input type="radio" id="subsidy_custom" v-model="subsidyModel.selected" value="custom" class="mr-1">
                        <input type="text" id="subsidy_custom_name" class="border ml-2 px-2 rounded" @focus="subsidyModel.selected = 'custom'" v-model="subsidyModel.custom.title"/><span> :</span>
                        <input type="number" min="1" @focus="subsidyModel.selected = 'custom'" oninput="this.value = !!this.value && Math.abs(this.value) >= 0 ? Math.abs(this.value) : null" id="subsidy_custom_amount" class="border ml-2 px-2 rounded w-20" v-model="subsidyModel.custom.amount"/><span>{{ currencySymbol }}</span>
                    </li>
                    <li>
                        <input type="radio" id="subsidy_no" v-model="subsidyModel.selected" value="no" class="mr-1">
                        <label for="subsidy_no" class="ml-2">{{ subsidyModel.no.title }}</label>
                    </li>
                </ul>
            </template>

            <template v-if="financingModel !== null && financingModel.isFinancingAvailable">
                <p class="mt-2">{{ $t('heatPumpOffersCreate.Financing') }}</p>
                <ul class="list" >
                    <li v-for="(financingOption, key) in financingModel.financingOptions">
                        <input type="radio" :id="`financing-${key}`" v-model="financingModel.selectedKey" :value="key" class="mr-1">
                        <label :for="`financing-${key}`">{{ financingOption.title }}</label>
                    </li>
                </ul>
            </template>

        </div>

        <div>
            <v-text-field
                v-model="offerBasis.annualConsumption"
                :label="$t('offerCommonOptions.Annual consumption')"
                variant="outlined"
                density="compact"
                disabled
            />

            <div>
                <breeze-label
                    :value="$t('offerCommonOptions.VAT rate')"
                />

                <div
                    v-for="(rate, key) in vatRates"
                    :key="`vat-rate-${key}`"
                    class="w-full">
                    <label
                        class="inline-flex items-center cursor-pointer">
                        <input
                            type="radio"
                            v-model="offerBasis.vat_level"
                            :value="rate / 100 + 1"
                            :disabled="readMode"
                            class="cursor-pointer"
                        />
                        <span class="ml-2 text-sm cursor-pointer select-none">
                            {{ $t("offerCommonOptions.VAT") }}
                            {{ rate }}%
                        </span>
                    </label>
                </div>
            </div>
        </div>

        <div>
            <v-select
                v-model.number="offerBasis.roof_sheathing"
                :label="$t('offerCommonOptions.Roofing')"
                :items="[
                    ...roofSheathings.map((o) => ({
                        title: o.name,
                        value: o.id,
                    })),
                ]"
                variant="outlined"
                density="compact"
                @change="updateLightningProtection"
                :disabled="readMode"
            />
            <image-input
                v-if="!readMode"
                v-model="offerBasis.imageUpload"
                @uploaded="addImage"
            />

        </div>

        <div>
            <label class="flex items-center" v-if="priceFactorsVars.stuckTilesPerKwp && priceFactorsVars.stuckTilesPerKwp > 0">
                <breeze-checkbox
                    name="stuck_tiles"
                    v-model:checked="offerBasis.stuck_tiles"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Nailed tile") }}
                </span>
            </label>

            <label class="flex items-center" v-if="priceFactorsVars.lightningRodRemovalPerKwp && priceFactorsVars.lightningRodRemovalPerKwp > 0">
                <breeze-checkbox
                    name="lightning_rod_removal"
                    v-model:checked="offerBasis.lightning_rod_removal"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Dismantling the lightning rod") }}
                </span>
            </label>

            <label class="flex items-center" v-if="priceFactorsVars.snowGuardRemovalPerKwp && priceFactorsVars.snowGuardRemovalPerKwp > 0">
                <breeze-checkbox
                    name="snow_guards"
                    v-model:checked="offerBasis.snow_guards"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Dismantling snow guards") }}
                </span>
            </label>

            <label class="flex items-center" v-if="priceFactorsVars.grounding && priceFactorsVars.grounding > 0">
                <breeze-checkbox
                    name="grounding"
                    v-model:checked="offerBasis.grounding"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Execution of earthing") }}
                </span>
            </label>

            <label class="flex items-center" v-if="priceFactorsVars.lightningRodRemovalPerKwp && priceFactorsVars.lightningRodRemovalPerKwp > 0">
                <breeze-checkbox
                    name="lightning_protection"
                    :disabled="lightningProtectionForced || readMode"
                    :class="{ 'opacity-50': lightningProtectionForced }"
                    v-model:checked="offerBasis.lightning_protection"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Lightning protection") }}
                </span>
            </label>

            <label class="flex items-center" v-if="priceFactorsVars.lift && priceFactorsVars.lift > 0">
                <breeze-checkbox
                    name="lift"
                    v-model:checked="offerBasis.lift"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Rent a lift") }}
                </span>
            </label>

            <label class="flex items-center" v-if="priceFactorsVars.insurance && priceFactorsVars.insurance > 0">
                <breeze-checkbox
                    name="insurance"
                    v-model:checked="offerBasis.insurance"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Add insurance") }}
                </span>
            </label>

            <label class="flex items-center" v-if="priceFactorsVars.diggingPerMeter && priceFactorsVars.diggingPerMeter > 0">
                <breeze-checkbox
                    name="digging"
                    v-model:checked="offerBasis.digging"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ $t("offerCommonOptions.Piercing") }}
                </span>
            </label>

            <label v-for="(extraCost, key) in extraCosts" class="flex items-center">
                <breeze-checkbox
                    v-model:checked="extraCost.selected"
                    :disabled="readMode"
                />

                <span class="ml-2 text-sm">
                    {{ extraCost.name }}
                </span>
            </label>

            <v-text-field
                v-if="offerBasis.digging"
                class="mt-4"
                v-model.number="offerBasis.digging_length"
                :label="$t('offerCommonOptions.The length of the ditch [m]')"
                variant="outlined"
                density="compact"
                type="number"
                min="0"
                :disabled="readMode"
                :checked="offerBasis.digging"
                oninput="this.value = !!this.value && Math.abs(this.value) >= 0 ? Math.abs(this.value) : null"
            />
        </div>
    </div>
</template>

<script>
import BreezeCheckbox from "@/Components/Checkbox";
import ImageInput from "./ImageInput";
import BreezeInput from "@/Components/Input";
import BreezeLabel from "@/Components/Label";

export default {
    props: {
        offerBasis: {
            type: Object,
            required: true,
        },
        roofSheathings: {
            type: Array,
            required: true,
        },
        extraCosts: {
            type: Array,
            required: false,
            default: () => []
        },
        readMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        priceFactors: {
          type: Array,
          required: true,
        },
        currencySymbol: {
            type: String,
            required: true,
        },
        subsidyModel: {
            type: Object,
            required: false,
            default: () => null
        },
        financingModel: {
            type: Object,
            required: false,
            default: () => null
        },
    },
    components: {
        BreezeCheckbox,
        BreezeInput,
        BreezeLabel,
        ImageInput,
    },
    setup(props) {
      const priceFactorsVars = Object.assign(
          {},
          ...props.priceFactors.map((o) => {
            let key = o.name_as_variable;
            let value = parseFloat(o.price);

            return { [key]: value };
          })
      )

      return { priceFactorsVars }
    },
    computed: {
        lightningProtectionForced() {
            return this.roofSheathings.find(
                (roofSheathing) => roofSheathing.id === this.offerBasis.roof_sheathing
            ).forces_lightning_protection;
        },
        vatRates() {
            switch (process.env.MIX_APP_LOCALE) {
                case "pl":
                    return [8, 23];
                default:
                    return [0, 6, 21];
            }
        },
    },
    methods: {
        addImage(image) {
            this.offerBasis.imageUpload = image;
        },
        updateLightningProtection() {
            if (this.lightningProtectionForced) {
                this.offerBasis.lightning_protection = true;
            }
        },
    },
};
</script>
